<template>
  <ion-page>
    <ion-content
      :class="[isMobile ? 'main-background account-content' : 'liga-web-background account-content account-content--web']"
      :fullscreen="true"
      :scroll-y="true"
    >
      <div class="account-page-body">
      <div class="logo">
        <img :src="accountLogoSrc" alt="liga-logo" />
      </div>
      <h1 class="ion-text-center account-page-title">ЛИЧНЫЙ КАБИНЕТ</h1>
      <div class="account-avatar-wrap" @click="loadAvatar()" title="Изменить фото">
        <div class="account-avatar-circle">
          <ion-avatar class="account-avatar">
            <ion-img :src="displayAvatarSrc"></ion-img>
          </ion-avatar>
          <span class="account-avatar-camera" aria-hidden="true">
            <ion-icon :icon="cameraOutline"></ion-icon>
          </span>
        </div>
        <span class="account-avatar-change">Изменить</span>
      </div>
      <div class="ion-text-center account-username">
        {{ lastname }} {{ firstname }}
      </div>
      <div class="account-info ion-margin-top">
        <div class="account-item">
          <div class="account-item-label">Логин</div>
          <div class="account-item-value">{{ username }}</div>
        </div>
        <div class="account-item">
          <div class="account-item-label">E-mail</div>
          <div class="account-item-value">{{ email }}</div>
        </div>
        <div class="account-item account-item-icon">
          <div class="account-item-label">Пароль</div>
          <div class="account-item-value">******</div>
          <ion-icon :icon="keyOutline" class="password-icon" @click="changePassword()"></ion-icon>
        </div>

        <div class="account-item account-item-privacy">
          <span class="account-item-label">Политика конфиденциальности</span>
          <ion-button fill="outline" size="small" class="privacy-link-btn" @click="$router.push('/privacy-policy')">
            Открыть
          </ion-button>
        </div>
        <div class="account-item account-item-consent" v-if="privacyConsentAccepted !== null">
          <div class="account-item-label">Согласие с условиями</div>
          <div class="account-item-value">{{ privacyConsentAccepted ? 'Принято' : 'Не принято' }}</div>
        </div>

        <div class="account-sessions-block">
          <div class="account-sessions-title">Авторизации на устройствах ({{ sessions.length }})</div>
          <p v-if="sessionsLoading" class="account-sessions-hint">Загрузка…</p>
          <div v-else-if="sessions.length" class="account-sessions-list">
            <div v-for="s in sessions" :key="s.id" class="account-session-item">
              <div class="account-session-info">
                <span class="account-session-device">{{ s.deviceName || 'Устройство' }}</span>
                <span class="account-session-date">{{ formatSessionDate(s.createdAt) }}</span>
              </div>
              <ion-button fill="outline" size="small" class="account-session-revoke" @click="revokeSession(s.id)">
                Выйти с устройства
              </ion-button>
            </div>
          </div>
          <p v-else class="account-sessions-hint">Нет активных авторизаций</p>
        </div>

        <ion-button class="account-signout-btn" v-on:click="signOut()">Выход</ion-button>

        <ion-button fill="outline" class="delete-account-button" v-on:click="confirmDeleteAccount()">
          Удалить аккаунт
        </ion-button>
      </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { LEGAL } from "@/config/legal";
import { GlobalContext } from "@/model/globalcontext.model";
import { ligaAccount } from "@/model/ligaaccount.model";
import AccountService from "@/services/account.service";
import store from "@/store/index";
import { resolveAvatarUrl } from "@/utils/avatar-url";
import { clearClientSessionAndRedirectGuest } from "@/utils/auth-session-clear";
import { Camera, CameraResultType, Photo } from "@capacitor/camera";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import {
    Directory,
    Encoding,
    Filesystem,
    ReadFileResult,
} from "@capacitor/filesystem";
import { Preferences } from "@capacitor/preferences";
import {
    IonAvatar,
    IonButton,
    IonContent,
    IonIcon,
    IonImg,
    IonPage,
    alertController,
} from "@ionic/vue";
import dayjs from "dayjs";
import { cameraOutline, keyOutline } from "ionicons/icons";
import { computed, defineComponent, inject } from "vue";
import logoWhite from "@/theme/liga-logo-white.png";
import logoBlack from "@/theme/liga-logo-black.png";
import defaultAvatar from "@/theme/fencer-2.png";

export default defineComponent({
  name: "Account",
  components: {
    IonContent,
    IonPage,
    IonAvatar,
    IonButton,
    IonIcon,
    IonImg,
  },
  setup() {
    const AVATAR_FILE_NAME = "liga-avatar.jpeg";
    const globalContext = inject("globalContext") as GlobalContext;
    const isMobile = globalContext.isMobile;
    const accountService = new AccountService(globalContext);
    const accountLogoSrc = computed(() =>
      (globalContext.effectiveTheme || "dark") === "light" ? logoBlack : logoWhite
    );

    return {
      cameraOutline,
      keyOutline,
      AVATAR_FILE_NAME,
      isMobile,
      globalContext,
      accountService,
      accountLogoSrc,
      defaultAvatar,
    };
  },
  data() {
    return {
      username: store.getters.account ? store.getters.account.login : "",
      firstname: store.getters.account ? store.getters.account.firstName : "",
      lastname: store.getters.account ? store.getters.account.lastName : "",
      email: store.getters.account ? store.getters.account.email : "",
      userId: store.getters.account
        ? Number(store.getters.account.id)
        : Number(-1),
      avatarUrl: "",
      ligaAccount: new ligaAccount(-1),
      privacyConsentAccepted: null as boolean | null,
      sessions: [] as { id: number; deviceName: string; createdAt: string; lastUsedAt: string | null }[],
      sessionsLoading: false,
    };
  },
  ionViewWillEnter() {
    this.username = store.getters.account ? store.getters.account.login : "";
    this.firstname = store.getters.account ? store.getters.account.firstName : "";
    this.lastname = store.getters.account ? store.getters.account.lastName : "";
    this.email = store.getters.account ? store.getters.account.email : "";
    Preferences.get({ key: LEGAL.privacyConsentKey }).then((res) => {
      this.privacyConsentAccepted = res.value === "true";
    });

    Filesystem.readdir({
      path: "",
      directory: Directory.Data,
    }).then((response) => {
      const avatarFile = response.files.find((f) => {
        return f.name == this.AVATAR_FILE_NAME;
      });
      if (undefined !== avatarFile) {
        Filesystem.getUri({
          path: this.AVATAR_FILE_NAME,
          directory: Directory.Data,
        }).then((res) => {
          if (res.uri !== "")
            this.avatarUrl = Capacitor.convertFileSrc(res.uri);
        });
      } else {
        const serverAvatar = resolveAvatarUrl(store.getters.account?.imageUrl);
        if (serverAvatar) this.avatarUrl = serverAvatar;
      }
    }).catch(() => {
      const serverAvatar = resolveAvatarUrl(store.getters.account?.imageUrl);
      if (serverAvatar) this.avatarUrl = serverAvatar;
    });

    if (!this.globalContext.offlineMode) {
      this.loadligaAccount(this.userId);
      this.loadSessions();
    }
  },

  computed: {
    displayAvatarSrc(): string {
      return this.avatarUrl || this.defaultAvatar;
    },
    medicalDateFormated(): string {
      return this.ligaAccount
        ? dayjs(this.ligaAccount.medicalBookDate).format("DD.MM.YYYY")
        : "Нет информации";
    },
    contractDays(): number {
      return this.ligaAccount && this.ligaAccount.contractStartDate
        ? dayjs().diff(this.ligaAccount.contractStartDate, "day")
        : 0;
    },
  },

  methods: {
    async showAlert(alertTitle: string, alertMessage: string) {
      const alert = await alertController.create({
        header: alertTitle,
        message: alertMessage,
        buttons: ["OK"],
      });

      await alert.present();
    },

    formatSessionDate(iso: string): string {
      if (!iso) return "";
      return dayjs(iso).format("DD.MM.YYYY HH:mm");
    },

    async loadSessions() {
      if (this.globalContext?.offlineMode) return;
      this.sessionsLoading = true;
      try {
        await this.accountService.ensureSession();
        this.sessions = await this.accountService.getSessions();
      } catch {
        this.sessions = [];
      } finally {
        this.sessionsLoading = false;
      }
    },

    async revokeSession(sessionId: number) {
      try {
        const ok = await this.accountService.revokeSession(sessionId);
        if (ok) {
          this.sessions = this.sessions.filter((s) => s.id !== sessionId);
        } else {
          await this.showAlert("Ошибка", "Не удалось завершить сессию.");
        }
      } catch (e) {
        console.error("revokeSession", e);
        await this.showAlert("Ошибка", "Не удалось завершить сессию.");
      }
    },

    signOut() {
      void clearClientSessionAndRedirectGuest(false, 'login');
    },

    changePassword() {
      this.$router.push("/change-password");
    },

    async confirmDeleteAccount() {
      const alert = await alertController.create({
        header: 'Удаление аккаунта',
        message: 'Вы уверены, что хотите удалить свой аккаунт? Это действие нельзя отменить. Все ваши данные будут удалены.',
        buttons: [
          {
            text: 'Отмена',
            role: 'cancel',
            cssClass: 'secondary',
          },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: () => {
              this.deleteAccount();
            },
          },
        ],
      });

      await alert.present();
    },

    async deleteAccount() {
      try {
        const success = await this.accountService.deleteAccount();
        if (success) {
          // Redirect to login after successful deletion
          this.$router.replace('/login');
        } else {
          await this.showAlert(
            'Ошибка',
            'Не удалось удалить аккаунт. Пожалуйста, попробуйте позже.'
          );
        }
      } catch (error) {
        console.error('Error deleting account:', error);
        await this.showAlert(
          'Ошибка',
          'Произошла ошибка при удалении аккаунта. Пожалуйста, попробуйте позже.'
        );
      }
    },

    async loadAvatar() {
      const isAndroid = Capacitor.getPlatform() === "android";
      const cameraPermissions = await Camera.checkPermissions();
      if (isAndroid) {
        // Галерея — Photo Picker без READ_MEDIA_*; для пункта «Камера» в prompt нужен только CAMERA.
        if (cameraPermissions.camera.toString() !== "granted") {
          await Camera.requestPermissions({ permissions: ["camera"] });
        }
      } else if (
        cameraPermissions.photos.toString() !== "granted" ||
        cameraPermissions.camera.toString() !== "granted"
      ) {
        const requestCameraPermissions = await Camera.requestPermissions();
        if (
          requestCameraPermissions.photos.toString() !== "granted" ||
          requestCameraPermissions.camera.toString() !== "granted"
        ) {
          this.showAlert(
            "Отмена",
            "Необходимо предоставить доступ приложению к камере и фото."
          );
          return;
        }
      }
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.Uri,
      });
      try {
        const saved = await this.savePicture(image, this.AVATAR_FILE_NAME);
        this.avatarUrl = saved.webviewPath ? saved.webviewPath : "";

        // Загрузка на сервер — чтобы аватар видели другие в Liga.Chat
        if (image.webPath) {
          const blob = await fetch(image.webPath).then(r => r.blob());
          const imageUrl = await this.accountService.uploadAvatar(blob, "avatar.jpg");
          if (imageUrl) {
            const account = store.getters.account ? { ...store.getters.account, imageUrl } : null;
            if (account) {
              store.commit("authenticated", account);
            }
          } else {
            await this.showAlert(
              "Внимание",
              "Фото сохранено на устройстве, но не удалось отправить на сервер. Другие пользователи пока не увидят аватар."
            );
          }
        }
      } catch (e) {
        console.error(e);
        await this.showAlert("Ошибка", "Не удалось сохранить аватар.");
      }
    },

    async loadligaAccount(userId: number) {
      const tokenQuery = await Preferences.get({ key: "jwt" });
      const token = tokenQuery.value;

      const options = {
        url: `${process.env.VUE_APP_API_URL}api/liga-accounts/user/${userId}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      };

      await CapacitorHttp.get(options)
        .then((response) => {
          if (response) {
            this.ligaAccount = response.data;
          }
        })
        .catch(() => {
          console.error("Failed to load liga account");
        });
    },

    async base64FromPath(path: string): Promise<string> {
      const response = await fetch(path);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
          if (typeof reader.result === "string") {
            resolve(reader.result);
          } else {
            reject("method did not return a string");
          }
        };
        reader.readAsDataURL(blob);
      });
    },

    async savePicture(photo: Photo, fileName: string) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const base64Data = await this.base64FromPath(photo.webPath!);
      let fsPermissions = false;
      await Filesystem.checkPermissions().then(async (res) => {
        if (res.publicStorage.toString() !== "granted") {
          Filesystem.requestPermissions().then(async (res) => {
            if (res.publicStorage.toString() !== "granted") {
              return;
            }
          });
        } else {
          fsPermissions = true;
        }
        if (fsPermissions) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          await Filesystem.writeFile({
            path: fileName,
            data: base64Data,
            directory: Directory.Data,
          });
        }
      });

      return {
        filepath: fileName,
        webviewPath: photo.webPath,
      };
    },

    async readFile(fileName: string): Promise<ReadFileResult> {
      const contents = await Filesystem.readFile({
        path: fileName,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });

      return contents;
    },
  },
});
</script>

<style scoped>
.account-page-body {
  width: 100%;
  box-sizing: border-box;
}

.account-content--web .account-page-body {
  max-width: 480px;
  margin: 0 auto;
  padding: 24px 20px 48px;
}

.logo {
  margin: 2vh 1vw 0 0;
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  min-height: 80px;
}

.logo img {
  width: 220px;
}

.account-page-title {
  margin: 0.4em 0 0;
  letter-spacing: 0.08em;
  color: var(--liga-chrome-text, #fff);
  font-weight: 800;
  font-size: 1.35rem;
}

.account-avatar-wrap {
  width: 100px;
  margin: 1.5em auto 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.account-avatar-circle {
  position: relative;
  width: 100px;
  height: 100px;
}

.account-avatar {
  width: 100px;
  height: 100px;
  background: var(--liga-float-glass-bg-strong, rgba(255, 255, 255, 0.9));
  border: 1px solid var(--liga-float-glass-border, rgba(255, 255, 255, 0.14));
  box-shadow: var(--liga-float-glass-shadow, 0 8px 24px rgba(0, 0, 0, 0.2));
  backdrop-filter: var(--liga-float-glass-blur, blur(18px));
  -webkit-backdrop-filter: var(--liga-float-glass-blur, blur(18px));
}

.account-avatar ion-img,
.account-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.account-avatar-camera {
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--liga-float-glass-bg-strong, rgba(255, 255, 255, 0.94));
  border: 1px solid var(--liga-float-glass-border, rgba(30, 30, 40, 0.16));
  box-shadow: var(--liga-float-glass-shadow, 0 4px 12px rgba(0, 0, 0, 0.18));
  pointer-events: none;
}

.account-avatar-camera ion-icon {
  color: var(--liga-chrome-text);
  font-size: 15px;
}

.account-avatar-change {
  margin-top: 0.45rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--ion-color-orange);
  letter-spacing: 0.02em;
}

.account-username {
  margin-top: 1em;
  letter-spacing: 0.04em;
  color: var(--liga-chrome-text, #fff);
  font-weight: 600;
  font-size: 1.05rem;
}

.account-info {
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: flex-start;
  padding-bottom: 2em;
}

.account-item,
.account-session-item {
  width: min(420px, 90vw);
  max-width: 100%;
  min-height: 2.75em;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  padding: 0.65em 1em;
  margin-bottom: 10px;
  border-radius: 16px;
  background: var(--liga-float-glass-bg-strong);
  border: 1px solid var(--liga-float-glass-border);
  box-shadow: var(--liga-float-glass-shadow);
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
  color: var(--liga-chrome-text);
}

.account-content--web .account-item,
.account-content--web .account-sessions-block,
.account-content--web .account-session-item {
  width: 100%;
}

.account-item-icon {
  justify-content: space-between;
}

.account-item-label {
  font-family: Montserrat, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--liga-chrome-text);
}

.account-item-value {
  font-family: Montserrat, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 0.82rem;
  letter-spacing: 0.02em;
  margin-left: 1.25em;
  color: var(--liga-chrome-text-muted);
  font-weight: 500;
}

.account-signout-btn {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --color: #fff;
  --border-radius: 14px;
  --box-shadow: 0 6px 16px rgba(235, 89, 82, 0.28);
  width: min(280px, 90vw);
  min-height: 48px;
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0.01em;
  margin-top: 4px;
}

.delete-account-button {
  --background: transparent;
  --background-activated: rgba(235, 68, 90, 0.12);
  --border-color: var(--ion-color-danger);
  --color: var(--ion-color-danger);
  --border-width: 1.5px;
  --border-radius: 14px;
  --box-shadow: none;
  width: min(280px, 90vw);
  min-height: 46px;
  margin-top: 10px;
  font-weight: 650;
  text-transform: none;
}

.password-icon {
  color: var(--ion-color-orange);
  margin-left: auto;
  flex-shrink: 0;
  font-size: 1.25rem;
}

.account-item-privacy,
.account-item-consent {
  justify-content: space-between;
  min-height: 3em;
}

.account-item-privacy .account-item-label {
  flex: 1;
  margin-right: 8px;
}

.privacy-link-btn,
.account-session-revoke {
  --background: transparent;
  --background-activated: rgba(235, 89, 82, 0.12);
  --border-color: var(--ion-color-orange);
  --color: var(--ion-color-orange);
  --border-width: 1.5px;
  --border-radius: 999px;
  --box-shadow: none;
  margin: 0;
  width: auto;
  min-height: 34px;
  height: 34px;
  flex-shrink: 0;
  font-weight: 650;
  font-size: 0.8rem;
  text-transform: none;
  letter-spacing: 0.01em;
}

.account-sessions-block {
  width: min(420px, 90vw);
  max-width: 100%;
  margin-bottom: 1em;
}

.account-sessions-title {
  display: block;
  margin: 0.4em 0 0.75em;
  color: var(--liga-chrome-text, #fff);
  font-weight: 750;
  font-size: 0.95rem;
  letter-spacing: 0.01em;
}

.account-sessions-hint {
  font-size: 0.85em;
  color: var(--liga-chrome-text-muted);
  margin: 0.5em 0;
}

.account-sessions-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.account-session-item {
  margin-bottom: 0;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
}

.account-session-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.account-session-device {
  font-weight: 650;
  font-size: 0.9em;
  color: var(--liga-chrome-text);
}

.account-session-date {
  font-size: 0.8em;
  color: var(--liga-chrome-text-muted);
}
</style>